import { getDoc, setDoc } from './store';
import type { GcalApagar, Task } from './types';
import { occursOn } from '../lib/recur';
import { addDays, brYmd, norm, parseYmd, today, uid } from '../lib/date';
import { autoEmoji } from '../lib/emoji';
import { paraLixeira } from '../lib/lixeira';
import { t as tr } from '../lib/i18n';

/** Duração padrão de uma tarefa nova (Ajustes > Abas > My Planner, 0.67); sem escolha, 30 minutos. */
const duracaoPadrao = () => { try { return getDoc('ajustes').duracaoPadrao || 30; } catch { return 30; } };

export function newTask(p: Partial<Task> = {}): Task {
  const now = new Date().toISOString();
  return {
    id: uid(), title: '', emoji: '📝', emojiManual: false, area: 'planner', color: null,
    start: today(), end: null, time: null, duration: duracaoPadrao(),
    recur: { type: 'none' }, alerts: { mode: 'start', before: [] }, energy: 1,
    link: null, pauses: [], subtasks: [], notes: '',
    done: [], subDone: {}, skip: [], createdAt: now, updatedAt: now,
    ...p,
    ...(p.title && !p.emoji ? { emoji: autoEmoji(p.title) } : {}),
  };
}

// ─── Mudanças "só hoje" / "só esta semana" de tarefas que se repetem (0.67, texto 05 B1) ───
/** Campos que podem ser diferentes numa ocorrência. Datas e frequência são da série inteira. */
export const CAMPOS_VARIAVEIS = ['title', 'emoji', 'emojiManual', 'area', 'color', 'time', 'duration', 'alerts', 'energy', 'link',
  'pauses', 'subtasks', 'notes', 'tz', 'anexos', 'insistente', 'pausaEmoji', 'pausaAviso'] as const satisfies readonly (keyof Task)[];
type CampoVariavel = typeof CAMPOS_VARIAVEIS[number];
const igual = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** A tarefa como ela é nesta data (com a mudança "só hoje"/"só esta semana", se houver).
 *  Use em todo lugar que mostra ou avisa uma ocorrência (lista do dia, avisos, pendências). */
export function naData(t: Task, date: string): Task {
  const ex = t.excecoes?.[date];
  return ex && Object.keys(ex).length ? { ...t, ...ex } : t;
}
/** Esta ocorrência foi mudada só para ela (ou só para a semana)? Mostra o ✎ no cartão (B2). */
export const mudadaNaData = (t: Task, date: string) => !!t.excecoes?.[date] && Object.keys(t.excecoes[date]).length > 0;

/** Campos variáveis em que b difere de a. */
export function camposMudados(a: Task, b: Task): CampoVariavel[] {
  return CAMPOS_VARIAVEIS.filter(k => !igual(a[k], b[k]));
}
/** Primeiro dia da semana de uma data, conforme Ajustes > Calendário ("A semana começa na"; padrão segunda). */
export function inicioDaSemana(date: string) {
  const dom = getDoc('ajustes').semanaComeca === 'dom';
  const dia = parseYmd(date).getDay();
  return addDays(date, -(dom ? dia : (dia + 6) % 7));
}

export type OndeAplicar = 'dia' | 'semana' | 'futuras';
/** Salva a edição de uma ocorrência de tarefa que se repete (B1).
 *  editada: a tarefa como ficou na ficha; aberta: como ela estava naquele dia quando a ficha abriu.
 *  'dia': só esta data; 'semana': as vezes desta semana (da data em diante e as que ainda não passaram);
 *  'futuras': divide a série (a antiga termina na véspera e a nova começa nesta data, com as mudanças).
 *  As vezes que já passaram nunca mudam (são o histórico dos relatórios). */
export function salvarOcorrencia(editada: Task, aberta: Task, date: string, onde: OndeAplicar): Task {
  const base = getDoc('planner').tasks.find(x => x.id === editada.id);
  if (!base) { saveTask(editada); return editada; }
  const mudados = camposMudados(aberta, editada);
  const agora = new Date().toISOString();
  if (onde === 'futuras') {
    const serieMudou = editada.start !== aberta.start || editada.end !== aberta.end || !igual(editada.recur, aberta.recur);
    const comMudancas: Task = { ...base, ...Object.fromEntries(mudados.map(k => [k, editada[k]])),
      start: editada.start, end: editada.end, recur: editada.recur };
    // começo da série (ou sem nenhuma vez antes desta data): a tarefa toda muda, como antes
    if (date <= base.start) {
      const ex = limparExcecoes(comMudancas, mudados);
      saveTask({ ...comMudancas, excecoes: ex, done: editada.done, subDone: editada.subDone });
      return comMudancas;
    }
    const inicio = serieMudou && editada.start > date ? editada.start : date;
    const depois = <V,>(r: Record<string, V> | undefined, fn: (d: string) => boolean) =>
      Object.fromEntries(Object.entries(r ?? {}).filter(([d]) => fn(d))) as Record<string, V>;
    const nova: Task = { ...comMudancas, id: uid(), gcal: undefined, start: inicio,
      done: editada.done.filter(d => d >= date), skip: base.skip.filter(d => d >= date), subDone: depois(editada.subDone, d => d >= date),
      excecoes: limparExcecoes({ ...comMudancas, excecoes: depois(base.excecoes, d => d >= date) }, mudados),
      createdAt: agora, updatedAt: agora };
    // a série antiga fica com o histórico (o que já foi feito) e termina na véspera;
    // as marcações de dias passados mudadas na ficha também valem (vêm da ficha, não da cópia salva)
    mut(base.id, x => ({ ...x, end: addDays(date, -1), done: editada.done.filter(d => d < date), skip: x.skip.filter(d => d < date),
      subDone: depois(editada.subDone, d => d < date), excecoes: depois(x.excecoes, d => d < date) }));
    setDoc('planner', d => ({ tasks: [...d.tasks, nova] }));
    return nova;
  }
  // 'dia' / 'semana': guarda só o que mudou, data por data
  const datas = [date];
  if (onde === 'semana') {
    const ini = inicioDaSemana(date), hoje = today();
    for (let i = 0; i < 7; i++) {
      const d = addDays(ini, i);
      if (d !== date && d >= hoje && occursOn(base, d)) datas.push(d);
    }
  }
  const excecoes = { ...(base.excecoes ?? {}) };
  for (const d of datas) {
    const ex: Partial<Task> = { ...(excecoes[d] ?? {}) };
    for (const k of mudados) (ex as any)[k] = editada[k];
    excecoes[d] = ex;
  }
  // done/subDone: o que foi marcado na ficha vale (é por data)
  mut(base.id, x => ({ ...x, done: editada.done, subDone: editada.subDone, excecoes: limparExcecoes({ ...x, excecoes }, CAMPOS_VARIAVEIS) }));
  return naData({ ...base, excecoes }, date);
}
/** Tira das exceções os campos que ficaram iguais à série (e as datas que ficaram vazias). */
function limparExcecoes(t: Task, campos: readonly CampoVariavel[]): Record<string, Partial<Task>> | undefined {
  if (!t.excecoes) return undefined;
  const out: Record<string, Partial<Task>> = {};
  for (const [d, ex] of Object.entries(t.excecoes)) {
    const e: Partial<Task> = { ...ex };
    for (const k of campos) if (k in e && igual(e[k], t[k])) delete e[k];
    if (Object.keys(e).length) out[d] = e;
  }
  return Object.keys(out).length ? out : undefined;
}

export const tasksOn = (date: string, tasks = getDoc('planner').tasks) =>
  tasks.filter(t => occursOn(t, date)).map(t => naData(t, date))
    .sort((a, b) => (a.time ?? '').localeCompare(b.time ?? '') || (a.ordem ?? 1e9) - (b.ordem ?? 1e9) || a.createdAt.localeCompare(b.createdAt));

/** Nova ordem das tarefas sem horário do dia (arrastando). */
export function ordenarTarefas(ids: string[]) {
  const pos = new Map(ids.map((id, i) => [id, i]));
  setDoc('planner', d => ({ tasks: d.tasks.map(t => (pos.has(t.id) && t.ordem !== pos.get(t.id) ? { ...t, ordem: pos.get(t.id)!, updatedAt: new Date().toISOString() } : t)) }));
}

const mut = (id: string, fn: (t: Task) => Task) =>
  setDoc('planner', d => ({ tasks: d.tasks.map(t => (t.id === id ? { ...fn(t), updatedAt: new Date().toISOString() } : t)) }));

export function saveTask(t: Task) {
  const existe = getDoc('planner').tasks.some(x => x.id === t.id);
  if (existe) mut(t.id, () => t);
  else setDoc('planner', d => ({ tasks: [...d.tasks, { ...t, updatedAt: new Date().toISOString() }] }));
}

export const toggleDone = (id: string, date: string) =>
  mut(id, t => ({ ...t, done: t.done.includes(date) ? t.done.filter(x => x !== date) : [...t.done, date] }));

export const toggleSub = (id: string, subId: string, date: string) =>
  mut(id, t => {
    const cur = t.subDone[date] ?? [];
    return { ...t, subDone: { ...t.subDone, [date]: cur.includes(subId) ? cur.filter(x => x !== subId) : [...cur, subId] } };
  });

export const addSub = (id: string, text: string, date?: string) =>
  mut(id, t => {
    const sub = { id: uid(), text: text.trim() };
    const ex = date ? t.excecoes?.[date] : undefined;
    // subtarefas mudadas só neste dia (0.67): a nova entra só nele
    if (date && ex?.subtasks) return { ...t, excecoes: { ...t.excecoes, [date]: { ...ex, subtasks: [...ex.subtasks, sub] } } };
    return { ...t, subtasks: [...t.subtasks, sub] };
  });

/** Exclusão: só esta ocorrência, esta e as futuras, ou a tarefa inteira.
 *  Devolve a exclusão posta na fila da agenda do Google (se a tarefa é ligada a ela). */
export function deleteTask(id: string, modo: 'one' | 'future' | 'all', date: string): GcalApagar | undefined {
  const t = getDoc('planner').tasks.find(x => x.id === id);
  if (!t) return;
  let fila: GcalApagar | undefined;
  // Tarefa ligada à agenda do Google: o celular que sincroniza apaga o evento lá também.
  if (t.gcal) {
    const g = t.gcal;
    const quando = (d: string) => new Date(`${d}T${t.time ?? '00:00'}:00`).toISOString();
    const item: GcalApagar = g.inst || modo === 'all' || (modo === 'future' && date <= t.start)
      ? { ev: g.ev, inst: g.inst, instStart: g.instStart }
      : { ev: g.ev, inst: date, instStart: quando(date), futuro: modo === 'future' };
    setDoc('ajustes', d => ({ ...d, gcalApagar: [...(d.gcalApagar ?? []), item].slice(-200) }));
    fila = item;
  }
  if (modo === 'all' || (modo === 'future' && date <= t.start)) {
    setDoc('planner', d => ({ tasks: d.tasks.filter(x => x.id !== id) }));
  } else if (modo === 'future') {
    mut(id, x => ({ ...x, end: addDays(date, -1) }));
  } else {
    mut(id, x => ({ ...x, skip: [...x.skip, date] }));
  }
  return fila;
}

/** Exclusão feita pela pessoa (ficha, cartão, modo seleção): vai para a Lixeira e mostra o Desfazer (texto 05 C1).
 *  A tarefa inteira volta como estava; "só esta" volta o dia; "esta e as futuras" volta a data de fim de antes.
 *  A exclusão posta na fila da agenda do Google vai junto, para ser cancelada ao restaurar. */
export function excluirTarefa(id: string, modo: 'one' | 'future' | 'all', date: string) {
  const tk = getDoc('planner').tasks.find(x => x.id === id);
  if (!tk) return;
  const g = deleteTask(id, modo, date);
  const extra = g ? { gcal: [g] } : undefined;
  if (modo === 'all' || (modo === 'future' && date <= tk.start)) paraLixeira('planner', 'tarefa', tk.title, tk, extra);
  else if (modo === 'future') paraLixeira('planner', 'tarefa-fim', tr('{nome} · de {data} em diante', { nome: tk.title, data: brYmd(date) }), { id, end: tk.end ?? null }, extra);
  else paraLixeira('planner', 'ocorrencias', tr('{nome} · {n} dia(s)', { nome: tk.title, n: 1 }), { id, dias: [date] }, extra);
}

/** Ao restaurar (Lixeira ou Desfazer): tira da fila da agenda do Google as exclusões que o celular ainda não fez.
 *  Devolve as que já tinham sido feitas na agenda. */
export function cancelarApagarGcal(itens?: GcalApagar[]): GcalApagar[] {
  if (!itens?.length) return [];
  const ch = (a: GcalApagar) => JSON.stringify([a.ev, a.inst ?? '', a.instStart ?? '', !!a.futuro]);
  const fila = (getDoc('ajustes').gcalApagar ?? []).map(ch);
  const feitas: GcalApagar[] = [], tirar: string[] = [];
  for (const it of itens) {
    const i = fila.indexOf(ch(it));
    if (i >= 0) { fila.splice(i, 1); tirar.push(ch(it)); } else feitas.push(it);
  }
  if (tirar.length) setDoc('ajustes', d => {
    const l = [...(d.gcalApagar ?? [])];
    for (const c of tirar) { const i = l.findIndex(x => ch(x) === c); if (i >= 0) l.splice(i, 1); }
    return { ...d, gcalApagar: l };
  });
  return feitas;
}

/** Energia somada das tarefas do dia (Relaxar = 0). */
export const energyOn = (date: string, tasks?: Task[]) => tasksOn(date, tasks).reduce((s, t) => s + (t.energy || 0), 0);

export function energyLimit(date: string) {
  const e = getDoc('ajustes').energia;
  if (e.modo === 'sem') return null;
  if (e.modo === 'todos') return e.todos;
  const [y, m, d] = date.split('-').map(Number);
  return e.dias[new Date(y, m - 1, d).getDay()];
}

// ─── Pendências dos dias anteriores (0.24) ───────────────────────────────────────────────────────
export type Pendencia = { t: Task; data: string };
const chavePend = (p: Pendencia) => `${p.t.id}@${p.data}`;

/** Tarefas dos últimos 7 dias que não foram concluídas (nem dispensadas). Tarefas que se repetem
 *  e também acontecem hoje ficam de fora (já estão na lista de hoje), assim como eventos do Google. */
export function pendencias(tasks: Task[], hoje = today(), dispensadas: string[] = []): Pendencia[] {
  const fora = new Set(dispensadas);
  const out: Pendencia[] = [];
  for (let i = 1; i <= 7; i++) {
    const d = addDays(hoje, -i);
    for (const t of tasks) {
      if (t.done.includes(d) || !occursOn(t, d)) continue;
      if (t.gcal?.origem === 'google') continue;
      if (t.recur.type !== 'none' && occursOn(t, hoje)) continue;
      if (t.end && t.end >= hoje && t.end !== t.start) continue;   // tarefa de vários dias que ainda está correndo
      const p = { t: naData(t, d), data: d };
      if (!fora.has(chavePend(p))) out.push(p);
    }
  }
  return out;
}

export function dispensarPend(ps: Pendencia[], desfazivel = false) {
  const novas = ps.map(chavePend);
  const hoje = today();
  setDoc('ajustes', d => ({ ...d, pendDispensadas: [...(d.pendDispensadas ?? []), ...novas].slice(-300),
    ...(desfazivel ? { pendDesfazer: { dia: hoje, chaves: [...(d.pendDesfazer?.dia === hoje ? d.pendDesfazer.chaves : []), ...novas] } } : {}) }));
}
/** 0.65 (Texto 4 A): desfaz as pendências dispensadas hoje (até o fim do dia). */
export function desfazerDispensa(chaves?: string[]) {
  setDoc('ajustes', d => {
    const hoje = today();
    const lista = d.pendDesfazer?.dia === hoje ? d.pendDesfazer.chaves : [];
    const volta = new Set(chaves ?? lista);
    return { ...d, pendDispensadas: (d.pendDispensadas ?? []).filter(k => !volta.has(k)), pendDesfazer: { dia: hoje, chaves: lista.filter(k => !volta.has(k)) } };
  });
}

/** Copia para hoje (a original fica como estava no dia dela). */
export function copiarParaHoje(p: Pendencia, hoje = today()) {
  const { id: _i, gcal: _g, excecoes: _x, ...resto } = p.t;
  const now = new Date().toISOString();
  saveTask({ ...resto, id: uid(), start: hoje, end: null, recur: { type: 'none' }, done: [], subDone: {}, skip: [], createdAt: now, updatedAt: now });
  dispensarPend([p]);
}

/** Move para hoje: a tarefa simples muda de data; a que se repete sai daquele dia e vira uma cópia hoje. */
export function moverParaHoje(p: Pendencia, hoje = today()) {
  if (p.t.recur.type === 'none' && !p.t.gcal) {
    mut(p.t.id, t => ({ ...t, start: hoje, end: null, done: t.done.filter(x => x !== p.data), subDone: { ...t.subDone, [hoje]: t.subDone[p.data] ?? [] } }));
    return;
  }
  const { id: _i, gcal: _g, excecoes: _x, ...resto } = p.t;
  const now = new Date().toISOString();
  saveTask({ ...resto, id: uid(), start: hoje, end: null, recur: { type: 'none' }, done: [], subDone: {}, skip: [], createdAt: now, updatedAt: now });
  mut(p.t.id, t => ({ ...t, skip: [...t.skip, p.data] }));
}

export const concluirPend = (p: Pendencia) => mut(p.t.id, t => ({ ...t, done: [...t.done, p.data] }));

/** Limpeza da agenda em massa (Ajustes > Abas > My Planner).
 *  de/ate: período (YYYY-MM-DD); sem "de" = tudo antes de "ate"; sem os dois = histórico completo.
 *  nome: só as tarefas cujo título contém o texto (sem diferença de acentos e maiúsculas). */
export type Limpeza = { de: string | null; ate: string | null; nome?: string };
export type PlanoLimpeza = { apagar: string[]; pular: Record<string, string[]>; tarefas: number; dias: number };

const LIMITE_DIAS = 4000;   // tarefas que se repetem: no máximo uns 11 anos de dias pulados por vez

export function planoLimpeza(l: Limpeza, tasks = getDoc('planner').tasks): PlanoLimpeza {
  const busca = l.nome ? norm(l.nome.trim()) : '';
  const r: PlanoLimpeza = { apagar: [], pular: {}, tarefas: 0, dias: 0 };
  for (const t of tasks) {
    if (busca && !norm(t.title).includes(busca)) continue;
    if (!l.de && !l.ate) { r.apagar.push(t.id); r.tarefas++; continue; }
    const repete = t.recur.type !== 'none' || !!t.end;
    const fim = t.recur.type === 'none' && !t.end ? t.start : t.end;   // última data possível (sem = para sempre)
    const dentro = (!l.de || t.start >= l.de) && !!fim && (!l.ate || fim <= l.ate);
    if (dentro) { r.apagar.push(t.id); r.tarefas++; continue; }
    if (!repete) continue;
    // Só uma parte da tarefa cai no período: pula os dias dela que estão dentro.
    let d = l.de && l.de > t.start ? l.de : t.start;
    const ate = [l.ate, t.end].filter(Boolean).sort()[0] as string | undefined;
    if (!ate) continue;
    const dias: string[] = [];
    for (let i = 0; d <= ate && i < LIMITE_DIAS; i++, d = addDays(d, 1)) if (occursOn(t, d)) dias.push(d);
    if (dias.length) { r.pular[t.id] = dias; r.dias += dias.length; }
  }
  return r;
}

/** Devolve, por tarefa, as exclusões postas na fila da agenda do Google (para cancelar ao restaurar). */
export function aplicarLimpeza(p: PlanoLimpeza): Record<string, GcalApagar[]> {
  const tasks = getDoc('planner').tasks;
  const apagar = new Set(p.apagar);
  // Tarefas ligadas à agenda do Google: o celular que sincroniza apaga lá também.
  const gcal: GcalApagar[] = [];
  const porTarefa: Record<string, GcalApagar[]> = {};
  for (const t of tasks) {
    if (!t.gcal) continue;
    const n0 = gcal.length;
    if (apagar.has(t.id)) gcal.push({ ev: t.gcal.ev, inst: t.gcal.inst, instStart: t.gcal.instStart });
    else for (const d of (p.pular[t.id] ?? []).slice(0, 50)) gcal.push({ ev: t.gcal.ev, inst: d, instStart: new Date(`${d}T${t.time ?? '00:00'}:00`).toISOString() });
    if (gcal.length > n0) porTarefa[t.id] = gcal.slice(n0);
  }
  if (gcal.length) setDoc('ajustes', d => ({ ...d, gcalApagar: [...(d.gcalApagar ?? []), ...gcal].slice(-200) }));
  const agora = new Date().toISOString();
  setDoc('planner', d => ({ tasks: d.tasks.filter(t => !apagar.has(t.id))
    .map(t => (p.pular[t.id] ? { ...t, skip: [...new Set([...t.skip, ...p.pular[t.id]])], updatedAt: agora } : t)) }));
  return porTarefa;
}
